import React, { useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import LinkExtension from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Message } from '../../../services/chatService/ChatService';
import TicketLinkPreview from '../../ui/Tooltip/TicketLinkPreview';
import ResourceLinkPreviews from '../resourceChat/ResourceLinkPreviews';
import TaskFaqLinkPreviews from '../resourceChat/TaskFaqLinkPreviews';

interface MessageRendererProps {
    message: Message;
    appearance?: "default" | "meeting-dark";
    automated?: boolean;
}

const MessageRenderer: React.FC<MessageRendererProps> = ({
    message,
    appearance = "default",
    automated = false,
}) => {
    const [parseError, setParseError] = useState(false);
    const isMeetingDark = appearance === "meeting-dark";

    // Highly resilient JSON "Onion Peeler" parser
    const tipTapDoc = useMemo(() => {

        setParseError(false);

        // Helper to safely parse escaped strings
        const safeParse = (str: string) => {
            try {
                let cleanStr = str.trim();
                // Strip outer quotes if it was double-stringified (e.g., "\"{\\\"v\\\":1}\"")
                if (cleanStr.startsWith('"') && cleanStr.endsWith('"')) {
                    cleanStr = cleanStr.slice(1, -1).replace(/\\"/g, '"');
                }
                return JSON.parse(cleanStr);
            } catch (e) {
                return null;
            }
        };

        // Recursive function to dig through wrappers and stringified layers
        const extractDoc = (payload: any, depth = 0): any => {

            if (depth > 5) {
                return null;
            }
            if (!payload) return null;

            // 1. If it's a string, parse it, then recurse
            if (typeof payload === 'string') {
                const parsed = safeParse(payload);
                return parsed ? extractDoc(parsed, depth + 1) : null;
            }

            // 2. If it's an object, check its shape
            if (typeof payload === 'object') {
                // Is this the actual raw TipTap structure?
                if (payload.type === 'doc' && payload.content) {
                    return payload;
                }
                
                // Does it have a nested 'doc' property? (Our wrapper from the PRD)
                if (payload.doc) {
                    return extractDoc(payload.doc, depth + 1);
                }
            }

            return null;
        };

        let extracted = null;

        // Try extracting from the dedicated column first
        if (message.messageDoc) {
            extracted = extractDoc(message.messageDoc);
        } 
        
        // Fallback: If it's in the plaintext column but looks like JSON
        if (!extracted && typeof message.messageContent === 'string' && message.messageContent.includes('"editor"')) {
            extracted = extractDoc(message.messageContent);
        }

        if (!extracted && (message.messageDoc || (typeof message.messageContent === 'string' && message.messageContent.includes('"editor"')))) {
            setParseError(true);
        }

        return extracted;
    }, [message]);

    const editor = useEditor({
        editable: false,
        content: tipTapDoc,
        extensions: [
            StarterKit.configure({ link: false }),
            Mention.configure({
                HTMLAttributes: {
                    class: 'chat-mention bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-md font-semibold inline-block not-prose no-underline pointer-events-none',
                },
                renderText({ node }: { node: any }) {
                    return node.attrs.mentionSuggestionChar === '#'
                        ? `#${node.attrs.label ?? node.attrs.id}`
                        : `@${node.attrs.label ?? node.attrs.id}`;
                },
                renderHTML({ node, options }: { node: any; options: any }) {
                    if (node.attrs.mentionSuggestionChar === '#') {
                        return ['a', {
                            href: `/task/${encodeURIComponent(String(node.attrs.id))}`,
                            class: 'chat-task-reference',
                            'data-task-reference': node.attrs.id,
                        }, `#${node.attrs.label ?? node.attrs.id}`];
                    }
                    return ['span', options.HTMLAttributes, `@${node.attrs.label ?? node.attrs.id}`];
                },
            }),
            LinkExtension.configure({
                openOnClick: true,
                HTMLAttributes: {
                    class: 'chat-message-link text-indigo-600 underline cursor-pointer hover:text-indigo-800 font-medium break-all',
                },
            }),
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        editorProps: {
            attributes: {
                class: `focus:outline-none break-words whitespace-pre-wrap max-w-full text-[13.5px] 
                        [&_p]:!m-0 [&_p]:!p-0 [&_p]:leading-relaxed 
                        [&>p:last-child:empty]:hidden [&>p:last-child:has(br:only-child)]:hidden`.replace(/\s+/g, ' '),
            },
        },
    });

    useEffect(() => {
        if (editor && tipTapDoc && !editor.isDestroyed) {
            editor.commands.setContent(tipTapDoc);
        }
    }, [tipTapDoc, editor]);

    // If it was supposed to be a TipTap doc but failed to parse, show an error state
    if (parseError) {
        return (
            <div
                className={`rounded-lg p-2 text-[13px] italic ${
                    isMeetingDark
                        ? "bg-red-950/30 text-red-300"
                        : "bg-red-50/50 text-red-500/80"
                }`}
            >
                Message formatting error. Check console for payload details.
            </div>
        );
    }

    // 1. Render Rich Text
    if (tipTapDoc) {
        if (!editor) return null; // Prevent flashing while TipTap initializes
        
        const richTextAppearance = isMeetingDark
            ? [
                "!text-slate-100",
                "[&_p]:!text-slate-100",
                "[&_li]:!text-slate-100",
                "[&_span]:!text-inherit",
                automated ? "[&_strong]:!text-purple-200" : "[&_strong]:!text-white",
                "[&_em]:!text-slate-100",
                "[&_h1]:!text-white",
                "[&_h2]:!text-white",
                "[&_h3]:!text-white",
                "[&_h4]:!text-white",
                "[&_h5]:!text-white",
                "[&_h6]:!text-white",
                "[&_blockquote]:!text-slate-200",
                "[&_code]:!text-slate-100",
                "[&_a]:!text-purple-300",
                "[&_a:hover]:!text-purple-200",
            ].join(" ")
            : [
                // Keep chat text deterministic in light mode. Legacy/global CSS
                // previously allowed rendered TipTap nodes to inherit text-white.
                "!text-slate-800",
                "[&_p]:!text-slate-800",
                "[&_li]:!text-slate-800",
                "[&_span]:!text-inherit",
                automated ? "[&_strong]:!text-[#5F3D9E]" : "[&_strong]:!text-slate-900",
                "[&_em]:!text-slate-800",
                "[&_h1]:!text-slate-900",
                "[&_h2]:!text-slate-900",
                "[&_h3]:!text-slate-900",
                "[&_h4]:!text-slate-900",
                "[&_h5]:!text-slate-900",
                "[&_h6]:!text-slate-900",
                "[&_blockquote]:!text-slate-700",
                "[&_code]:!text-slate-800",
                "[&_a]:!text-[#6F49B5]",
                "[&_a:hover]:!text-[#5D3D99]",
            ].join(" ");

        return (
            <><TicketLinkPreview theme={isMeetingDark ? 'dark' : undefined}>
                <div
                    className={`chat-message-content ${automated ? 'chat-message-content--automated' : ''} ${isMeetingDark ? 'chat-message-content--meeting-dark' : ''} w-full max-w-full overflow-hidden tiptap-protect tiptap-renderer ${automated ? "[&_p]:!leading-[1.5] [&_p+p]:!mt-3 [&_ul+p]:!mt-3 [&_ul]:!my-1 [&_ul]:!pl-5 [&_li]:!my-1 [&_li]:!pl-1 [&_strong]:!font-semibold" : ""} ${richTextAppearance}`}
                >
                    <EditorContent
                        editor={editor}
                        className={`max-w-full ${richTextAppearance}`}
                    />
                </div>
            </TicketLinkPreview><ResourceLinkPreviews doc={tipTapDoc} appearance={isMeetingDark ? 'dark' : undefined} /><TaskFaqLinkPreviews doc={tipTapDoc} appearance={isMeetingDark ? 'dark' : undefined} /></>
        );
    }

    // 2. Render Legacy Plaintext Fallback
    return (
        <div
            className={`chat-message-content max-w-full whitespace-pre-wrap break-words break-all text-[13.5px] ${
                isMeetingDark ? "!text-slate-100" : "!text-slate-800"
            }`}
        >
            {message.messageContent}
        </div>
    );
};

export default MessageRenderer;
